import xregexp from 'xregexp'

const WordSplitCharRegEx = xregexp('^[\\s\u05be\u05bf,]$')

/**
 * Characters that split words in AnnotatedText but join words into a single
 * phrase — they should never cause trope-level phrase breaks.
 *   \u05BE = maqaf (Hebrew hyphen)
 *   \u05BF = rafe
 */
const JOINING_SEPARATORS = new Set(['\u05BE', '\u05BF'])

function isVowel(c: string): boolean {
  return !!c.match(/[\u05B0-\u05BD]/) || !!c.match(/[\u05C1-\u05C5]/) || !!c.match(/[\u05C7]/)
}

function isCantillation(c: string): boolean {
  return !!c.match(/[\u0591-\u05AF]/) || !!c.match(/\u05C0/)
}

function isCharacter(c: string): boolean {
  return !isVowel(c) && !isCantillation(c)
}

function isSeparator(c: string): boolean {
  return !!xregexp.match(c, WordSplitCharRegEx)
}

/** Split text into words by word-separator characters (same boundaries as AnnotatedSentence). */
function splitIntoWords(text: string): string[] {
  const words: string[] = []
  let current = ''
  for (const char of text) {
    if (isSeparator(char)) {
      if (current) {
        words.push(current)
        current = ''
      }
    } else {
      current += char
    }
  }
  if (current) {
    words.push(current)
  }
  return words
}

/** Does raw sentence text end with a section marker ({פ}, {ס}, (ס), etc.)? */
const SECTION_MARKER_RE = /[\s\u00a0]*[{(][פס][})]\s*$/
function hasSectionMarker(text: string): boolean {
  return SECTION_MARKER_RE.test(text)
}

// ─── Pre-compiled regexes for normalizeSentenceText ──────────────
const PASEQ_RE = xregexp('\\s*\u05c0\\s*', 'gu')
const MULTI_MAQAF_RE = xregexp('\u05be+', 'gu')
const MULTI_RAFE_RE = xregexp('\u05bf+', 'gu')
const BRACKET_RE = xregexp('([^\u05be\u05bf\\s]+)\\s+\\[(\\S+)\\]', 'gu')

/**
 * Canonical normalization of raw Hebrew sentence text.
 *
 * Both the trope-based phrase parser (versePhraser) and the rendering engine
 * (AnnotatedSentence) must agree on word boundaries.  Raw Sefaria text
 * contains formatting that can silently change the word count — most notably
 * the paseq (׀ U+05C0), which appears as a standalone space-delimited token
 * in the database but should be attached to its preceding word.
 *
 * Call this function on `sentence.text` BEFORE counting or splitting words.
 * It does NOT strip vowels or cantillation marks.
 */
const normalizeSentenceText = (text: string, { keepSofPasuq = false } = {}): string => {
  // Remove trailing section markers: (ס), {פ}, etc.
  text = text.replace(/\s*\(.\)$/, '')
  text = text.replace(/\s*\{.\}$/, '')

  // Sof pasuq (׃) has no trope significance; strip it unless the caller
  // explicitly needs the end-of-verse marker for display.
  if (!keepSofPasuq) {
    text = text.replace(/\s*[:׃]+\s*$/, '')
  }

  // Remove trailing comma
  text = text.replace(/\s*,\s*$/, '')

  // Paseq / munach-legarme: raw Sefaria text has " ׀ " (space-delimited).
  // Attach it to the preceding word so it doesn't inflate the word count.
  text = xregexp.replace(text, PASEQ_RE, '\u05c0 ')

  // Collapse duplicate whitespace and word-joining separators
  text = text.replace(/\s+/g, ' ')
  text = xregexp.replace(text, MULTI_MAQAF_RE, '\u05be')
  text = xregexp.replace(text, MULTI_RAFE_RE, '\u05bf')
  text = text.trim()

  // Bracket substitution: "word [replacement]" → "replacement"
  text = xregexp.replace(text, BRACKET_RE, '$2')

  return text
}

export {
  hasSectionMarker,
  isCantillation,
  isCharacter,
  isSeparator,
  isVowel,
  JOINING_SEPARATORS,
  normalizeSentenceText,
  splitIntoWords,
  WordSplitCharRegEx,
}
